import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import RegisteredAppointmentsTable, {
  AppointmentsApiResponse,
} from "./AppointmentsList";
import api from "../api.js";
import "../style/css/AppointmentsList.css";

const AppointmentsPage: React.FC = () => {
  const [appointmentsData, setAppointmentsData] =
    useState<AppointmentsApiResponse>({
      count: 0,
      next: null,
      previous: null,
      results: [],
      confirmed_appointments_count: 0,
      pending_appointments_count: 0,
      cancelled_appointments_count: 0,
      completed_appointments_count: 0,
    });

  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [timeFilter, setTimeFilter] = useState<string>("upcoming");
  const [basePath, setBasePath] = useState<string>(
    "/appointments/filtered-appointments/",
  );
  const [paginationParams, setPaginationParams] = useState<string>("?page=1");
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Scroll to top effects
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [paginationParams]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [statusFilter, timeFilter]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [searchQuery]);

  // **FIXED** Function to build the API URL
  const buildApiUrl = useCallback(
    (
      path: string,
      status: string,
      time: string,
      pageParams: string,
      search: string,
    ): string => {
      let params = new URLSearchParams(pageParams.replace("?", ""));

      params.set("time_filter", time);

      if (status !== "all") {
        params.set("appointments_status", status);
      } else {
        params.delete("appointments_status");
      }

      // **ADDED: Actually use the search parameter**
      if (search && search.trim() !== "") {
        params.set("search", search.trim());
      } else {
        params.delete("search"); // Optional: clear if empty
      }

      return `${path}?${params.toString()}`;
    },
    [],
  );

  const navigate = useNavigate();

  // Current URL with all parameters including search
  const currentUrl = useMemo(() => {
    return buildApiUrl(
      basePath,
      statusFilter,
      timeFilter,
      paginationParams,
      searchQuery,
    );
  }, [
    basePath,
    statusFilter,
    timeFilter,
    paginationParams,
    searchQuery,
    buildApiUrl,
  ]);

  // Effect to fetch data when currentUrl changes
  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        const response = await api.get(`${currentUrl}`);
        setAppointmentsData(response.data);
      } catch (error) {}
    };
    fetchAppointments();
  }, [currentUrl]);

  // Handlers for filter changes
  const handleStatusFilterChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    setPaginationParams("?page=1");
  };

  const handleTimeFilterChange = (newTime: string) => {
    setTimeFilter(newTime);
    setPaginationParams("?page=1");
  };

  // Search handler
  const handleSearchChange = (newSearchQuery: string) => {
    setSearchQuery(newSearchQuery);
    setPaginationParams("?page=1");
  };

  // Clear search handler
  const handleClearSearch = () => {
    setSearchQuery("");
    setPaginationParams("?page=1");
  };

  // Pagination Handlers
  const handleNextPage = () => {
    if (appointmentsData.next) {
      const nextQueryParams = new URL(appointmentsData.next).search;
      setPaginationParams(nextQueryParams);
    }
  };

  const handlePreviousPage = () => {
    if (appointmentsData.previous) {
      const previousQueryParams = new URL(appointmentsData.previous).search;
      setPaginationParams(previousQueryParams);
    }
  };

  const handleNewAppointment = () => {
    navigate("/appointment/new");
  };

  const handleView = (id: string) => alert(`Viewing appointment: ${id}`);
  const handleEdit = (id: string) => alert(`Editing appointment: ${id}`);

  return (
    <RegisteredAppointmentsTable
      data={appointmentsData}
      onNextPage={handleNextPage}
      onPreviousPage={handlePreviousPage}
      onNewAppointmentClick={handleNewAppointment}
      onViewAppointment={handleView}
      onEditAppointment={handleEdit}
      currentStatusFilter={statusFilter}
      onStatusFilterChange={handleStatusFilterChange}
      currentTimeFilter={timeFilter}
      onTimeFilterChange={handleTimeFilterChange}
      searchQuery={searchQuery}
      onSearchChange={handleSearchChange}
      onClearSearch={handleClearSearch}
    />
  );
};

export default AppointmentsPage;
